/**
 * Auth & Users State (Microservicio de Autenticación y Cuentas)
 * Conectado con los endpoints reales:
 * - POST /api/auth/login
 * - POST /api/auth/logout
 * - POST /api/auth/refresh
 * - GET /api/users/me
 */
import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { User, LeagueSummary } from '../models/types';
import { authApi } from '../http/auth.api';
import { registerSessionExpiredHandler } from '../http/api-simulator.service';
import { UiState } from './ui.state';

@Injectable({
  providedIn: 'root',
})
export class AuthState {
  private readonly ui = inject(UiState);
  private readonly router = inject(Router);

  readonly currentUser = signal<User | null>(this.getInitialUser());
  readonly isAuthLoading = signal<boolean>(false);
  readonly isAuthenticated = computed(() => !!this.currentUser());

  // Callback / hook for when leagues are received from auth login
  private onLoginLeaguesReceived?: (leagues: LeagueSummary[]) => void;
  // Hook ejecutado cuando la sesión expira automáticamente por 401
  private onSessionExpiredHook?: () => void;
  // Hook ejecutado al realizar logout para limpiar estados de juego
  private onLogoutHook?: () => void;

  constructor() {
    registerSessionExpiredHandler(async () => {
      await this.handleSessionExpired();
    });
  }

  setLeaguesHandler(handler: (leagues: LeagueSummary[]) => void): void {
    this.onLoginLeaguesReceived = handler;
  }

  setSessionExpiredHook(hook: () => void): void {
    this.onSessionExpiredHook = hook;
  }

  setLogoutHook(hook: () => void): void {
    this.onLogoutHook = hook;
  }

  async handleSessionExpired(): Promise<void> {
    if (!this.currentUser()) {
      return;
    }
    console.warn('[AuthState] Sesión expirada por 401 no recuperable. Cerrando sesión y redirigiendo...');
    if (this.onSessionExpiredHook) {
      try {
        this.onSessionExpiredHook();
      } catch (e) {
        console.error('Error en onSessionExpiredHook:', e);
      }
    }
    await this.logout(false);
    this.ui.showNotification(
      'Tu sesión ha expirado. Por favor, inicia sesión de nuevo.',
      'error'
    );
    this.router.navigate(['/login']);
  }

  async login(user: string, password?: string): Promise<User> {
    this.isAuthLoading.set(true);
    try {
      const res = await authApi.login({ email: user, password });
      const loggedUser: User = res.user;
      this.currentUser.set(loggedUser);
      if (this.onLoginLeaguesReceived && res?.leagues?.length) {
        this.onLoginLeaguesReceived(res.leagues);
      }
      this.ui.showNotification(
        `¡Sesión iniciada con éxito como ${loggedUser.name}!`,
        'success'
      );
      return loggedUser;
    } catch (err: any) {
      if (err?.code !== 'email_not_verified' && err?.responseBody?.code !== 'email_not_verified') {
        this.ui.showNotification(
          err?.message || 'Error al autenticar en el servidor',
          'error'
        );
      }
      throw err;
    } finally {
      this.isAuthLoading.set(false);
    }
  }

  async register(payload: { email: string; password: string }): Promise<void> {
    this.isAuthLoading.set(true);
    try {
      await authApi.register(payload);
    } finally {
      this.isAuthLoading.set(false);
    }
  }

  async resendRegistration(payload: { email: string }): Promise<void> {
    this.isAuthLoading.set(true);
    try {
      await authApi.resendRegistration(payload);
    } finally {
      this.isAuthLoading.set(false);
    }
  }

  async verifyRegistration(payload: { token: string }): Promise<void> {
    this.isAuthLoading.set(true);
    try {
      await authApi.verifyRegistration(payload);
    } finally {
      this.isAuthLoading.set(false);
    }
  }

  async handleSocialLoginSuccess(userData: { id: string; email: string; username?: string | null }): Promise<User> {
    const name = userData.username && userData.username.trim() ? userData.username.trim() : userData.email.split('@')[0];
    const loggedUser: User = {
      id: userData.id,
      name,
      email: userData.email,
      role: 'Director Técnico & Club Manager',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80',
    };
    this.currentUser.set(loggedUser);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('fm_real_api_user', JSON.stringify(loggedUser));
      localStorage.removeItem('fm_real_api_token');
    }
    this.ui.showNotification(
      `¡Sesión iniciada con éxito como ${loggedUser.name}!`,
      'success'
    );
    return loggedUser;
  }

  async logout(showNotification: boolean = true): Promise<void> {
    try {
      await authApi.logout();
    } catch (err) {
      console.warn('[AuthState] Error durante authApi.logout:', err);
    } finally {
      this.currentUser.set(null);
      if (this.onLogoutHook) {
        try {
          this.onLogoutHook();
        } catch (e) {
          console.error('[AuthState] Error ejecutando onLogoutHook:', e);
        }
      }
      if (typeof localStorage !== 'undefined') {
        const theme = localStorage.getItem('fm_theme');
        const lang = localStorage.getItem('fm_language');
        localStorage.clear();
        if (theme) localStorage.setItem('fm_theme', theme);
        if (lang) localStorage.setItem('fm_language', lang);
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
      }
      if (showNotification) {
        this.ui.showNotification('Sesión cerrada correctamente', 'info');
      }
    }
  }

  private getInitialUser(): User | null {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return null;
    }
    try {
      const saved = localStorage.getItem('fm_real_api_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }
}
